import React, { useCallback, useEffect, useRef, useState } from "react"
import { useClickOutside } from "@ciq/shared/hooks/useClickOutside"
import { FILTER_OPTIONS } from "../constants"

/**
 * Header for the file list with select-all checkbox, file/selection count, and status filter.
 *
 * @param {Object} props
 * @param {number} props.fileCount - Total number of files displayed
 * @param {number} props.selectedCount - Number of currently selected files
 * @param {boolean} props.allSelected - Boolean indicating if all files are selected
 * @param {Function} props.onSelectAll - Callback when select-all checkbox toggled
 * @param {string|null} props.statusFilter - Current status filter value or null for all
 * @param {Function} props.onStatusFilterChange - Callback when filter changes, receives status string or null
 * @returns {React.ReactElement} The file list header component
 */
export function FileListHeader({
  fileCount,
  selectedCount,
  allSelected,
  onSelectAll,
  statusFilter,
  onStatusFilterChange,
}) {
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef(null)
  const checkboxRef = useRef(null)
  const filterButtonRef = useRef(null)

  // S2: Set indeterminate state when some but not all files are selected
  const someSelected = selectedCount > 0 && !allSelected
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = someSelected
    }
  }, [someSelected])

  const closeFilter = useCallback(() => setFilterOpen(false), [])
  useClickOutside(filterRef, filterOpen, closeFilter)

  const handleFilterSelect = useCallback((value) => {
    onStatusFilterChange(value)
    setFilterOpen(false)
  }, [onStatusFilterChange])

  const handleFilterKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setFilterOpen(false)
      filterButtonRef.current?.focus()
      return
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      const options = filterRef.current?.querySelectorAll("[role='option']")
      if (!options?.length) return
      const focused = document.activeElement
      const idx = Array.from(options).indexOf(focused)
      const next = e.key === "ArrowDown"
        ? (idx + 1) % options.length
        : (idx - 1 + options.length) % options.length
      options[next]?.focus()
    }
  }, [])

  const headerText = selectedCount > 0 ? `${selectedCount} selected` : `Files (${fileCount})`

  const currentFilterLabel = FILTER_OPTIONS.find(o => o.value === statusFilter)?.label || "All"

  return (
    <div className="file-list-header">
      <input
        ref={checkboxRef}
        type="checkbox"
        className="file-checkbox"
        checked={allSelected}
        onChange={onSelectAll}
        aria-label="Select all files"
      />
      <span className="file-list-header-title">{headerText}</span>
      <div className="file-list-header-actions">
        <div className="status-filter" ref={filterRef}>
          <button
            ref={filterButtonRef}
            className="status-filter-button"
            onClick={() => setFilterOpen(prev => !prev)}
            aria-label={`Status filter: ${currentFilterLabel}`}
            aria-haspopup="listbox"
            aria-expanded={filterOpen}
            type="button"
          >
            Status: {currentFilterLabel}
          </button>
          {filterOpen && (
            <div
              className="status-filter-dropdown"
              role="listbox"
              aria-label="Filter by status"
              onKeyDown={handleFilterKeyDown}
            >
              {FILTER_OPTIONS.map(option => (
                <div
                  key={option.label}
                  className={`status-filter-option${option.value === statusFilter ? " selected" : ""}`}
                  role="option"
                  aria-selected={option.value === statusFilter}
                  tabIndex={0}
                  onClick={() => handleFilterSelect(option.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleFilterSelect(option.value) } }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
